import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Megaphone,
  Plus,
  Trash2,
  Pencil,
  X,
  AlertCircle,
  Search,
  Calendar,
  MoreVertical,
  BookOpen,
  Users,
  ChevronLeft,
  ChevronRight,
  FileText,
  CheckCircle2,
  Bell,
  Pin,
} from 'lucide-react';
import { Tabs } from '@/components/ui/Tabs';
import { instituteNoticesApi } from '@/features/institute/notices/api/instituteNotices.api';
import { formatTimeAgo } from '@/lib/format';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { Option } from '@/constants/institute';
import {
  ALL_CLASSES,
  NOTICE_AUDIENCE_LABEL,
  NOTICE_TYPES,
  NOTICE_PLACEHOLDERS,
} from '@/features/teacher/types/notices';
import type {
  NoticeAudience,
  NoticeStatus,
  NoticeType,
  TeacherNotice,
} from '@/features/teacher/types/notices';
import { noticesApi } from '@/features/teacher/api/notices.api';
import { teacherApi } from '@/features/teacher/api/teacher.api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import {
  distinctSections,
  formatInstituteDate,
  formatInstituteDateTime,
  instituteToday,
  toAllocatedClass,
} from '@/features/teacher/utils';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { TEACHING_ROLES } from '@/constants/roles';
import { cn } from '@/lib/utils';

type DateFilter = 'all' | 'today' | 'custom';
type StatusTab = 'all' | 'published' | 'draft' | 'deleted';

const DATE_FILTERS: readonly DateFilter[] = ['all', 'today', 'custom'];
const STATUS_TABS: readonly StatusTab[] = ['all', 'published', 'draft', 'deleted'];

/** How many notices a page of the board holds, and what else can be chosen. */
const PAGE_SIZES = [5, 10, 20, 50] as const;

/**
 * The board's filters, as they sit in the address bar, so leaving the board
 * and coming back finds the same tab, day, class, subject, search and page.
 *
 * `day` says whether a day is chosen at all — `today`, or `custom` with the
 * day itself in `date`. Class and subject are names read off the teacher's own
 * allotments; there is no fixed set to check them against.
 */
type Filters = {
  tab: 'class' | 'institute';
  status: StatusTab;
  day: DateFilter;
  date: string;
  class: string;
  subject: string;
  search: string;
  page: number;
  limit: number;
};

const DEFAULT_FILTERS: Filters = {
  tab: 'class',
  status: 'all',
  day: 'all',
  date: '',
  class: 'all',
  subject: 'all',
  search: '',
  page: 1,
  limit: 10,
};

const FILTER_OPTIONS = {
  allowed: {
    tab: ['class', 'institute'] as const,
    status: STATUS_TABS,
    day: DATE_FILTERS,
    limit: PAGE_SIZES,
  },
};

const NOTICE_TYPE_CARD_THEME: Record<
  NoticeType,
  {
    label: string;
    iconBg: string;
    iconColor: string;
    iconBorder: string;
    badgeBg: string;
    badgeColor: string;
    badgeBorder: string;
    dot: string;
    btnActive: string;
  }
> = {
  GENERAL: {
    label: 'General',
    iconBg: 'bg-blue-500/10 dark:bg-blue-500/15',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBorder: 'border-blue-500/20 dark:border-blue-500/30',
    badgeBg: 'bg-blue-500/10 dark:bg-blue-950/50',
    badgeColor: 'text-blue-700 dark:text-blue-300',
    badgeBorder: 'border-blue-500/25 dark:border-blue-800/50',
    dot: 'bg-blue-500',
    btnActive: 'bg-blue-500/15 border-blue-500/40 text-blue-700 dark:text-blue-300',
  },
  EVENT: {
    label: 'Event',
    iconBg: 'bg-emerald-500/10 dark:bg-emerald-500/15',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    iconBorder: 'border-emerald-500/20 dark:border-emerald-500/30',
    badgeBg: 'bg-emerald-500/10 dark:bg-emerald-950/50',
    badgeColor: 'text-emerald-700 dark:text-emerald-300',
    badgeBorder: 'border-emerald-500/25 dark:border-emerald-800/50',
    dot: 'bg-emerald-500',
    btnActive: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300',
  },
  REMINDER: {
    label: 'Reminder',
    iconBg: 'bg-purple-500/10 dark:bg-purple-500/15',
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBorder: 'border-purple-500/20 dark:border-purple-500/30',
    badgeBg: 'bg-purple-500/10 dark:bg-purple-950/50',
    badgeColor: 'text-purple-700 dark:text-purple-300',
    badgeBorder: 'border-purple-500/25 dark:border-purple-800/50',
    dot: 'bg-purple-500',
    btnActive: 'bg-purple-500/15 border-purple-500/40 text-purple-700 dark:text-purple-300',
  },
  ACADEMIC: {
    label: 'Academics',
    iconBg: 'bg-indigo-500/10 dark:bg-indigo-500/15',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    iconBorder: 'border-indigo-500/20 dark:border-indigo-500/30',
    badgeBg: 'bg-indigo-500/10 dark:bg-indigo-950/50',
    badgeColor: 'text-indigo-700 dark:text-indigo-300',
    badgeBorder: 'border-indigo-500/25 dark:border-indigo-800/50',
    dot: 'bg-indigo-500',
    btnActive: 'bg-indigo-500/15 border-indigo-500/40 text-indigo-700 dark:text-indigo-300',
  },
  IMPORTANT: {
    label: 'Important',
    iconBg: 'bg-rose-500/10 dark:bg-rose-500/15',
    iconColor: 'text-rose-600 dark:text-rose-400',
    iconBorder: 'border-rose-500/20 dark:border-rose-500/30',
    badgeBg: 'bg-rose-500/10 dark:bg-rose-950/50',
    badgeColor: 'text-rose-700 dark:text-rose-300',
    badgeBorder: 'border-rose-500/25 dark:border-rose-800/50',
    dot: 'bg-rose-500',
    btnActive: 'bg-rose-500/15 border-rose-500/40 text-rose-700 dark:text-rose-300',
  },
};

function getNoticeIcon(notice: TeacherNotice) {
  const titleLower = notice.title.toLowerCase();
  if (
    notice.noticeType === 'IMPORTANT' ||
    titleLower.includes('urgent') ||
    titleLower.includes('important')
  ) {
    return <AlertCircle className="h-5 w-5" />;
  }
  if (
    notice.noticeType === 'REMINDER' ||
    titleLower.includes('reminder') ||
    titleLower.includes('holiday')
  ) {
    return <Bell className="h-5 w-5" />;
  }
  if (
    notice.noticeType === 'EVENT' ||
    titleLower.includes('event') ||
    titleLower.includes('meeting') ||
    titleLower.includes('ptm')
  ) {
    if (titleLower.includes('meeting') || titleLower.includes('parent')) {
      return <Users className="h-5 w-5" />;
    }
    return <Calendar className="h-5 w-5" />;
  }
  if (
    notice.noticeType === 'ACADEMIC' ||
    titleLower.includes('test') ||
    titleLower.includes('exam') ||
    titleLower.includes('math') ||
    titleLower.includes('science') ||
    titleLower.includes('unit') ||
    titleLower.includes('academic')
  ) {
    return <BookOpen className="h-5 w-5" />;
  }
  return <Megaphone className="h-5 w-5" />;
}

export function TeacherNoticesPage() {
  const user = useAuthStore((s) => s.user);

  // The school's today, read on every render rather than once when the module
  // loaded: a tab left open past midnight must move on to the new day, and the
  // device's own zone is not the school's.
  const today = instituteToday();

  const canHoldClasses = Boolean(user && TEACHING_ROLES.includes(user.role));
  const { data: allocations } = useApiQuery(
    () => (canHoldClasses ? teacherApi.myAllocations() : Promise.resolve([])),
    [canHoldClasses],
  );

  /** The sections this teacher takes — the only ones a notice may be posted to. */
  const sections = useMemo(
    () => distinctSections((allocations ?? []).map(toAllocatedClass)),
    [allocations],
  );

  /**
   * The subjects this teacher takes, once each. Empty for a class-teacher-only
   * allotment — the server refuses a subject the caller does not take, so there
   * is nothing else a notice could honestly be tagged with.
   */
  const teacherSubjects = useMemo(() => {
    const byUuid = new Map<string, string>();
    for (const allocation of allocations ?? []) {
      if (allocation.subject) byUuid.set(allocation.subject.uuid, allocation.subject.name);
    }
    return Array.from(byUuid, ([uuid, name]) => ({ uuid, name }));
  }, [allocations]);

  const teachesSeveralSubjects = teacherSubjects.length > 1;

  /** The class filter options */
  const classFilterOptions = useMemo<Option[]>(
    () => [
      { value: 'all', label: 'All Classes' },
      { value: ALL_CLASSES, label: 'All Classes (General)' },
      ...sections.map((section) => ({ value: section.name, label: section.name })),
    ],
    [sections],
  );

  /** The subject filter options */
  const subjectFilterOptions = useMemo<Option[]>(
    () => [
      { value: 'all', label: 'All Subjects' },
      ...teacherSubjects.map((sub) => ({ value: sub.name, label: sub.name })),
    ],
    [teacherSubjects],
  );

  /** Target class options for modal form */
  const targetClassOptions = useMemo<Option[]>(
    () => [
      { value: '', label: ALL_CLASSES },
      ...sections.map((section) => ({ value: section.uuid, label: section.name })),
    ],
    [sections],
  );

  // ── Filters & Tabs ────────────────────────────────────────────────────────
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const {
    tab: boardTab,
    status: statusTab,
    day: dateFilter,
    date: customDate,
    class: classFilter,
    subject: subjectFilter,
    search: searchQuery,
    page,
    limit: pageSize,
  } = filters;

  // Any change to what is listed starts the reader on page 1 again — done
  // where the change is made, so arriving does not throw a restored page away.
  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  // ── Action Menu State ─────────────────────────────────────────────────────
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenuId(null);
      }
    }
    if (activeMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeMenuId]);

  /**
   * The day the list is narrowed to, or nothing for the whole board.
   */
  const isInstituteTab = boardTab === 'institute';

  // ── Institute Notices (Second Tab) ──────────────────────────────────────────
  const [instituteDate, setInstituteDate] = useState<string>('');
  const [institutePage, setInstitutePage] = useState<number>(1);
  const [instituteLimit, setInstituteLimit] = useState<number>(10);

  const {
    data: instituteData,
    isLoading: instituteIsLoading,
    error: instituteError,
    refetch: refetchInstitute,
  } = useApiQuery(
    () => instituteNoticesApi.list(instituteDate || undefined),
    [instituteDate],
  );

  const instituteNotices = instituteData?.notices ?? [];

  const sortedInstituteNotices = useMemo(() => {
    return [...instituteNotices].sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return new Date(b.noticeDate).getTime() - new Date(a.noticeDate).getTime();
    });
  }, [instituteNotices]);

  const instituteTotalCount = sortedInstituteNotices.length;
  const instituteTotalPages = Math.ceil(instituteTotalCount / instituteLimit) || 1;
  const instituteFromIndex = instituteTotalCount === 0 ? 0 : (institutePage - 1) * instituteLimit + 1;
  const instituteToIndex = Math.min(institutePage * instituteLimit, instituteTotalCount);

  const paginatedInstituteNotices = useMemo(() => {
    const start = (institutePage - 1) * instituteLimit;
    return sortedInstituteNotices.slice(start, start + instituteLimit);
  }, [sortedInstituteNotices, institutePage, instituteLimit]);

  const onDate =
    dateFilter === 'today'
      ? today
      : dateFilter === 'custom' && customDate
        ? customDate
        : undefined;

  const { data, isLoading, error, refetch } = useApiQuery(() => noticesApi.list({ onDate }), [onDate]);
  const notices = useMemo(() => data?.notices ?? [], [data]);

  /**
   * What has been taken down, for the Deleted tab. A separate read: the live
   * board never carries deleted notices, so the class and the parents never
   * see them, while the teacher can still look back at what was withdrawn.
   */
  const {
    data: deletedData,
    isLoading: isLoadingDeleted,
    error: deletedError,
    refetch: refetchDeleted,
  } = useApiQuery(() => noticesApi.list({ onDate, status: 'DELETED' }), [onDate]);
  const deletedNotices = useMemo(() => deletedData?.notices ?? [], [deletedData]);

  // ── Create & Edit form state ──────────────────────────────────────────────
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState<TeacherNotice | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formSection, setFormSection] = useState('');
  /** The subject's uuid, or empty for a notice that is not about one subject. */
  const [formSubject, setFormSubject] = useState('');
  const [formDate, setFormDate] = useState(today);
  /**
   * Who it is for. Opens on both: a class notice is usually something the
   * family needs to know too, and parents are only told about — and only see —
   * notices addressed to them.
   */
  const [formAudience, setFormAudience] = useState<NoticeAudience>('BOTH');
  const [isSaving, setIsSaving] = useState(false);

  // ── Delete confirm ────────────────────────────────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const deletingNotice = useMemo(() => {
    if (!deleteConfirmId) return null;
    return (
      notices.find((n) => n.uuid === deleteConfirmId) ??
      deletedNotices.find((n) => n.uuid === deleteConfirmId) ??
      null
    );
  }, [deleteConfirmId, notices, deletedNotices]);
  const [isDeleting, setIsDeleting] = useState(false);

  const placeholder = NOTICE_PLACEHOLDERS[Math.floor(Math.random() * NOTICE_PLACEHOLDERS.length)];

  const resetForm = () => {
    setEditingNotice(null);
    setFormTitle('');
    setFormBody('');
    setFormSection('');
    setFormSubject('');
    setFormDate(today);
    setFormAudience('BOTH');
    setIsFormOpen(false);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const handleOpenEditModal = (notice: TeacherNotice) => {
    setEditingNotice(notice);
    setFormTitle(notice.title);
    setFormBody(notice.body || '');
    setFormSection(notice.classSectionUuid || '');
    setFormSubject(notice.subjectUuid ?? '');
    setFormDate(notice.noticeDate || today);
    setFormAudience(notice.audience);
    setIsFormOpen(true);
  };

  /** Publishes a draft, or takes a published notice back to drafts — on the server. */
  const toggleNoticeDraft = async (notice: TeacherNotice) => {
    setActiveMenuId(null);
    const toDraft = notice.status === 'PUBLISHED';
    try {
      await noticesApi.update(notice.uuid, { status: toDraft ? 'DRAFT' : 'PUBLISHED' });
      toast.success(
        toDraft ? 'Moved to drafts' : 'Notice published',
        toDraft ? 'Your class no longer sees it until you publish it again.' : 'Your class can see it now.',
      );
      refetch();
    } catch (caught) {
      toast.error(
        toDraft ? 'Could not move this notice to drafts' : 'Could not publish this notice',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    }
  };

  /**
   * Saves the form as a draft or as a posted notice.
   *
   * An edit updates the notice in place. It used to delete the notice and post
   * a new one, which threw away its draft state and left a deleted copy behind
   * for every correction.
   */
  const handleSaveNotice = async (status: NoticeStatus) => {
    if (!formTitle.trim() || isSaving) return;
    if (!editingNotice && formDate < today) return;
    setIsSaving(true);

    try {
      if (editingNotice) {
        // Sent only when it changed: a subject the teacher no longer takes is
        // refused by the server, and re-sending it untouched would block an
        // edit to the title alone.
        const subjectChanged = formSubject !== (editingNotice.subjectUuid ?? '');
        const saved = await noticesApi.update(editingNotice.uuid, {
          classSectionUuid: formSection || null,
          ...(subjectChanged && { subjectUuid: formSubject || null }),
          // Type left out, so the server keeps it: General is the only kind a
          // teacher posts now, but an older Event or Academics notice is still
          // one, and fixing its title must not quietly re-file it as General.
          audience: formAudience,
          title: formTitle.trim(),
          body: formBody.trim() || null,
          noticeDate: formDate,
          status,
        });

        toast.success(
          status === 'DRAFT' ? `“${saved.title}” saved as a draft` : `“${saved.title}” updated`,
          `${saved.targetClass} · ${NOTICE_TYPES[saved.noticeType].label}`,
        );
      } else {
        const created = await noticesApi.create({
          ...(formSection && { classSectionUuid: formSection }),
          noticeType: 'GENERAL',
          ...(formSubject && { subjectUuid: formSubject }),
          audience: formAudience,
          title: formTitle.trim(),
          ...(formBody.trim() && { body: formBody.trim() }),
          noticeDate: formDate,
          status,
        });

        toast.success(
          status === 'DRAFT' ? `“${created.title}” saved as a draft` : `“${created.title}” is up`,
          status === 'DRAFT'
            ? 'Only you can see it. Publish it from Drafts when it is ready.'
            : `${created.targetClass} · ${NOTICE_TYPES[created.noticeType].label}`,
        );
      }

      resetForm();
      refetch();
    } catch (caught) {
      toast.error(
        editingNotice ? 'Could not update this notice' : 'Could not post this notice',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId || isDeleting) return;
    setIsDeleting(true);

    try {
      await noticesApi.remove(deleteConfirmId);
      toast.success('That notice has been taken down', 'Your class no longer sees it.');
      setDeleteConfirmId(null);
      refetch();
      refetchDeleted();
    } catch (caught) {
      toast.error(
        'Could not take this notice down',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Filtered notices ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const source = statusTab === 'deleted' ? deletedNotices : notices;
    return source.filter((n) => {
      const isDraft = n.status === 'DRAFT';

      // Status tab filter
      if (statusTab === 'published' && isDraft) return false;
      if (statusTab === 'draft' && !isDraft) return false;

      // Class filter
      if (classFilter !== 'all' && n.targetClass !== classFilter) return false;

      // Subject filter
      if (subjectFilter !== 'all' && n.subject !== subjectFilter) return false;

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (!n.title.toLowerCase().includes(q) && !n.body.toLowerCase().includes(q)) return false;
      }

      return true;
    });
  }, [notices, deletedNotices, statusTab, classFilter, subjectFilter, searchQuery]);

  /**
   * The subject choices in the form. An edited notice keeps its own subject in
   * the list even when the teacher no longer takes it, so the field shows what
   * the notice says rather than silently reading "None".
   */
  const formSubjectOptions = useMemo<Option[]>(() => {
    const options = teacherSubjects.map((sub) => ({ value: sub.uuid, label: sub.name }));
    const own = editingNotice?.subjectUuid;
    if (own && editingNotice.subject && !options.some((option) => option.value === own)) {
      options.push({ value: own, label: editingNotice.subject });
    }
    return [{ value: '', label: 'None / General' }, ...options];
  }, [teacherSubjects, editingNotice]);

  // Status Counts
  const totalNoticesCount = notices.length;
  const draftsCount = notices.filter((n) => n.status === 'DRAFT').length;
  const publishedCount = totalNoticesCount - draftsCount;
  const deletedCount = deletedNotices.length;

  /** The tabs, from one list — four near-identical buttons was four places to drift. */
  const statusTabs: { key: StatusTab; label: string; count: number; badge: string }[] = [
    { key: 'all', label: 'All', count: totalNoticesCount, badge: 'bg-surface-3 text-ink-3 border border-line' },
    { key: 'published', label: 'Published', count: publishedCount, badge: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
    { key: 'draft', label: 'Drafts', count: draftsCount, badge: 'bg-blue-500/15 text-blue-600 dark:text-blue-400' },
    { key: 'deleted', label: 'Deleted', count: deletedCount, badge: 'bg-rose-500/15 text-rose-600 dark:text-rose-400' },
  ];
  const isDeletedTab = statusTab === 'deleted';
  const listIsLoading = isDeletedTab ? isLoadingDeleted : isLoading;
  const listError = isDeletedTab ? deletedError : error;

  // Pagination calculation
  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safeCurrentPage = Math.min(page, totalPages);
  const fromIndex = totalCount === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const toIndex = Math.min(safeCurrentPage * pageSize, totalCount);

  const paginatedNotices = useMemo(() => {
    return filtered.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);
  }, [filtered, safeCurrentPage, pageSize]);

  /**
   * The next upcoming notice based on noticeDate (earliest noticeDate >= today).
   * Pinned and highlighted so the teacher immediately spots the next scheduled notice.
   */
  const nextUpcomingNoticeUuid = useMemo(() => {
    if (isDeletedTab) return null;
    const upcoming = filtered
      .filter((n) => {
        if (n.deletedAt !== null) return false;
        const d = (n.noticeDate || today).slice(0, 10);
        return d >= today;
      })
      .sort((a, b) => {
        const dateA = (a.noticeDate || today).slice(0, 10);
        const dateB = (b.noticeDate || today).slice(0, 10);
        const dateDiff = dateA.localeCompare(dateB);
        if (dateDiff !== 0) return dateDiff;
        if (a.status === 'PUBLISHED' && b.status !== 'PUBLISHED') return -1;
        if (b.status === 'PUBLISHED' && a.status !== 'PUBLISHED') return 1;
        return (b.createdAt || '').localeCompare(a.createdAt || '');
      });
    return upcoming[0]?.uuid ?? null;
  }, [filtered, isDeletedTab, today]);

  const hasActiveFilter =
    dateFilter !== 'all' ||
    Boolean(customDate) ||
    classFilter !== 'all' ||
    subjectFilter !== 'all' ||
    Boolean(searchQuery.trim());

  return (
    <div className="flex flex-col gap-6">
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className={cn(
            "grid h-12 w-12 shrink-0 place-items-center rounded-[12px] border",
            isInstituteTab
              ? "bg-blue-500/10 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400 border-blue-500/20"
              : "bg-indigo-500/10 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400 border-indigo-500/20"
          )}>
            <Megaphone className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-ink">
              {isInstituteTab ? "Institute Notices" : "Class Notices"}
            </h1>
            <p className="mt-0.5 text-sm text-ink-3 font-medium">
              {isInstituteTab
                ? "Official announcements and circulars published by the institute."
                : "Create, manage and view all notices for your class."}
            </p>
          </div>
        </div>

        {/* Right Controls: Search bar + Create Notice Button (Only on Class Notices tab) */}
        {!isInstituteTab && (
          <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
            {/* Search Bar */}
            <div className="flex items-center gap-2 bg-surface px-3 h-10 rounded-field border border-line border-2  min-w-0 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20 transition-all w-52 sm:w-64">
              <Search className="h-4 w-4 text-ink-3 shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => updateFilters({ search: e.target.value })}
                placeholder="Search notices..."
                className="bg-transparent text-sm font-medium text-ink placeholder:text-ink-3 outline-none w-full"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => updateFilters({ search: '' })}
                  className="shrink-0 text-ink-3 hover:text-ink cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Create Notice Button */}
            <Button
              variant="primary"
              size="md"
              onClick={handleOpenCreateModal}
              id="new-notice-page-btn"
              className="h-10 px-5 text-[14px] font-bold shadow-xs shrink-0 rounded-field inline-flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Notice
            </Button>
          </div>
        )}
      </div>

      {/* ── Tabs (Class Notices vs Institute Notices) & Filter ──────────── */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between border-b border-slate-200 dark:border-slate-800 gap-2 sm:gap-4">
        <Tabs
          label="Notice board tabs"
          options={[
            { value: 'class', label: 'Class Notices' },
            { value: 'institute', label: 'Institute Notices' },
          ]}
          value={boardTab}
          onChange={(val) => updateFilters({ tab: val })}
          className="border-b-0 px-0"
        />

        {isInstituteTab && (
          <div className="flex items-center gap-2 shrink-0 mb-2 sm:mb-2.5">
            <DatePicker
              label="Filter by date"
              hideLabel
              size="sm"
              
              placeholder={formatInstituteDate(today)}
              value={instituteDate}
              onChange={(e) => {
                setInstituteDate(e.target.value);
                setInstitutePage(1);
              }}
              className={cn(
                "h-9 w-40 rounded-field text-sm font-bold shadow-xs transition-all",
                "border border-indigo-500/40 dark:border-indigo-400/50",
                "bg-indigo-50/50 dark:bg-indigo-950/30",
                "text-indigo-950 dark:text-indigo-100",
                "[&>svg]:text-indigo-600 dark:[&>svg]:text-indigo-400",
                "hover:border-indigo-500 dark:hover:border-indigo-400 hover:bg-indigo-50/70 dark:hover:bg-indigo-950/50",
                Boolean(instituteDate) && "ring-2 ring-indigo-500/20 border-indigo-500 font-extrabold",
              )}
            />

            {instituteDate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInstituteDate('');
                  setInstitutePage(1);
                }}
                className="h-9 px-3 text-sm font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-pill cursor-pointer"
              >
                Clear filter
              </Button>
            )}
          </div>
        )}
      </div>


      {!isInstituteTab && (
        <div className="flex flex-col gap-6">
      {/* ── Tabs & Filter Bar ─────────────────────────────────────────────── */}
      <div className="card flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-[16px] bg-surface border border-line shadow-2xs">
        {/* Status Tabs + Secondary Filters */}
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {/* Status Tabs */}
          <div
            role="tablist"
            aria-label="Notice status"
            className="flex items-center bg-surface-2 border border-line rounded-[10px] p-1 gap-1 shrink-0 overflow-x-auto"
          >
            {statusTabs.map((tab) => {
              const isActive = statusTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => updateFilters({ status: tab.key })}
                  className={cn(
                    'flex items-center gap-2 px-3.5 py-1.5 rounded-[7px] text-[13px] font-semibold transition-all whitespace-nowrap cursor-pointer',
                    isActive ? 'bg-indigo-600 text-white shadow-xs' : 'text-ink-2 hover:text-ink hover:bg-surface/60',
                  )}
                >
                  <span>{tab.label}</span>
                  <span
                    className={cn(
                      'px-1.5 py-0.2 rounded-pill text-[11px] font-bold',
                      isActive ? 'bg-white/20 text-white' : tab.badge,
                    )}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Vertical Divider between Tabs and Filters */}
          <div className="h-7 w-px bg-line-strong shrink-0 mx-2 sm:mx-3" aria-hidden="true" />

          {/* Today Pill Button */}
          <button
            type="button"
            onClick={() => {
              if (dateFilter === 'today') {
                updateFilters({ day: 'all', date: '' });
              } else {
                updateFilters({ day: 'today', date: '' });
              }
            }}
            className={cn(
              'px-4 h-9 rounded-pill border text-sm font-semibold transition-all whitespace-nowrap cursor-pointer shadow-2xs',
              dateFilter === 'today'
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs font-bold'
                : 'bg-surface border-line text-ink hover:bg-surface-2',
            )}
          >
            Today
          </button>

          {/* Date Picker Button / Input */}
          <div className="shrink-0">
            <DatePicker
              label="Filter by date"
              hideLabel
              size="sm"
              placeholder={formatInstituteDate(today)}
              value={customDate}
              onChange={(e) => {
                const val = e.target.value;
                updateFilters({ date: val, day: val ? 'custom' : 'all' });
              }}
              className="h-9 w-40 border-line bg-surface text-slate-900 dark:text-slate-100 [&>svg]:text-slate-800 dark:[&>svg]:text-slate-200 text-sm font-semibold shadow-2xs"
            />
          </div>

          {/* Class Filter */}
          {sections.length > 0 && (
            <div className="w-36 shrink-0">
              <Select
                label="Class"
                hideLabel
                options={classFilterOptions}
                value={classFilter}
                onChange={(e) => updateFilters({ class: e.target.value })}
                className="h-9 text-xs font-semibold"
              />
            </div>
          )}

          {/* Subject (Optional) Filter — only with two subjects to tell apart. */}
          {teachesSeveralSubjects && (
            <div className="w-36 shrink-0">
              <Select
                label="Subject"
                hideLabel
                options={subjectFilterOptions}
                value={subjectFilter}
                onChange={(e) => updateFilters({ subject: e.target.value })}
                className="h-9 text-xs font-semibold"
              />
            </div>
          )}
        </div>

        {/* Clear Filters Button */}
        <Button
          variant={hasActiveFilter ? 'dangerSoft' : 'secondary'}
          size="sm"
          disabled={!hasActiveFilter}
          leftIcon={
            <X
              className={cn(
                'h-3.5 w-3.5 transition-colors',
                hasActiveFilter ? 'text-rose-600 dark:text-rose-400' : 'text-ink-3 opacity-60',
              )}
            />
          }
          onClick={() => {
            updateFilters({ day: 'all', date: '', class: 'all', subject: 'all', search: '' });
          }}
          className={cn(
            'h-9 px-3 text-xs font-semibold transition-colors shrink-0',
            hasActiveFilter
              ? 'border-rose-500/40 bg-rose-500/10 text-rose-600 hover:border-rose-500 hover:bg-rose-500/20 hover:text-rose-700 dark:border-rose-500/40 dark:text-rose-400 dark:hover:bg-rose-500/20'
              : 'opacity-50 cursor-not-allowed',
          )}
        >
          Clear filters
        </Button>
      </div>

      {/* ── Notices List ─────────────────────────────────────────────────── */}
      {listIsLoading && (
        <div className="card p-6 rounded-card bg-surface border border-line">
          <SkeletonLines count={6} />
        </div>
      )}

      {!listIsLoading && listError && (
        <div className="card flex flex-col items-start gap-3 p-6 rounded-card bg-surface border border-line">
          <Alert tone="critical" title={isDeletedTab ? 'Could not load your deleted notices' : 'Could not load your notices'}>
            {listError.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={isDeletedTab ? refetchDeleted : refetch}>
            Try again
          </Button>
        </div>
      )}

      {!listIsLoading && !listError && paginatedNotices.length === 0 && (
        <div className="card p-14 flex flex-col items-center gap-4 text-center rounded-card bg-surface border border-line shadow-2xs">
          <div className="h-16 w-16 rounded-[16px] bg-indigo-500/10 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400 border border-indigo-500/20 grid place-items-center">
            <Megaphone className="h-8 w-8 opacity-60" />
          </div>
          <div>
            <p className="text-[15px] font-bold text-ink">
              {isDeletedTab
                ? 'No deleted notices'
                : statusTab === 'draft'
                ? 'No drafts found'
                : hasActiveFilter
                  ? 'No notices match your filters'
                  : 'No notices posted yet'}
            </p>
            <p className="text-xs text-ink-3 font-medium mt-1">
              {isDeletedTab
                ? 'Notices you delete are kept here for your records. Your classes no longer see them.'
                : statusTab === 'draft'
                ? 'Save a notice as a draft to finish it later — your classes will not see it until you publish it.'
                : hasActiveFilter
                  ? 'Try adjusting the filters or search query above.'
                  : 'Create a notice to keep your students informed.'}
            </p>
          </div>
          {!hasActiveFilter && !isDeletedTab && (
            <Button variant="primary" size="sm" onClick={handleOpenCreateModal}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Create First Notice
            </Button>
          )}
        </div>
      )}

      {!listIsLoading && !listError && paginatedNotices.length > 0 && (
        <div className="flex flex-col gap-3">
          {paginatedNotices.map((notice) => {
            const cardTheme = NOTICE_TYPE_CARD_THEME[notice.noticeType] || NOTICE_TYPE_CARD_THEME.GENERAL;
            const isMenuOpen = activeMenuId === notice.uuid;
            // Only the poster may change or withdraw it — the server enforces the
            // same rule, this just stops offering what would be refused.
            const canEdit = notice.isMine;
            const isDraft = notice.status === 'DRAFT';
            const isDeleted = notice.deletedAt !== null;
            const subject = notice.subject;
            const isNextUpcoming = notice.uuid === nextUpcomingNoticeUuid;

            return (
              <div
                key={notice.uuid}
                className={cn(
                  'card group relative flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 sm:p-5 rounded-[16px] transition-all',
                  isNextUpcoming
                    ? 'border-2 border-indigo-500 dark:border-indigo-400 ring-4 ring-indigo-500/10 shadow-sm !bg-indigo-50/80 dark:!bg-indigo-950/40'
                    : 'bg-surface border border-line hover:border-indigo-500/30 hover:shadow-sm',
                )}
              >
                {/* Left Icon + Notice Main Info */}
                <div className="flex items-start gap-3.5 sm:gap-4 flex-1 min-w-0">
                  {/* Left Icon Box — matches theme vibrant styling */}
                  <div
                    className={cn(
                      'grid h-12 w-12 shrink-0 place-items-center rounded-[12px] border transition-all duration-200 group-hover:scale-105 shadow-2xs',
                      cardTheme.iconBg,
                      cardTheme.iconBorder,
                      cardTheme.iconColor,
                    )}
                  >
                    {getNoticeIcon(notice)}
                  </div>

                  {/* Body Info */}
                  <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-pill border',
                          cardTheme.badgeBg,
                          cardTheme.badgeBorder,
                          cardTheme.badgeColor,
                        )}
                      >
                        <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', cardTheme.dot)} />
                        {cardTheme.label}
                      </span>

                      {isNextUpcoming && (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-pill bg-indigo-600 text-white shadow-xs">
                          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse shrink-0" />
                          Next Upcoming
                        </span>
                      )}
                    </div>

                    <h3 className="text-[15px] font-bold text-ink tracking-tight leading-snug break-words">
                      {notice.title}
                    </h3>

                    {notice.body && (
                      <p className="text-[13px] text-ink-2 font-normal leading-relaxed break-words line-clamp-2">
                        {notice.body}
                      </p>
                    )}

                    {/* Meta info row */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-3 font-medium mt-0.5">
                      {notice.postedBy && (
                        <>
                          <span>By <strong className="text-ink-2 font-semibold">{notice.postedBy}</strong></span>
                          <span>•</span>
                        </>
                      )}
                      <span className="inline-flex items-center gap-1 font-semibold text-ink-2 bg-surface-2 px-2 py-0.5 rounded-[6px] border border-line">
                        {notice.targetClass}
                      </span>
                      {subject && (
                        <>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1 font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded-pill border border-indigo-500/20">
                            <BookOpen className="h-3 w-3" />
                            {subject}
                          </span>
                        </>
                      )}
                      <span>•</span>
                      <span className="inline-flex items-center gap-1 text-ink-3">
                        <Users className="h-3 w-3 inline text-ink-3" />
                        {NOTICE_AUDIENCE_LABEL[notice.audience]}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Status, Date, Actions */}
                <div className="flex items-center md:flex-col md:items-end justify-between md:justify-center gap-2 sm:gap-3 shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-line/60">
                  <div className="flex items-center gap-2">
                    {/* Status Badge */}
                    {isDeleted ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 dark:text-rose-300 bg-rose-500/10 dark:bg-rose-950/60 px-2.5 py-1 rounded-pill border border-rose-500/25 dark:border-rose-800/60">
                        <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        Deleted
                      </span>
                    ) : isDraft ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 dark:text-blue-300 bg-blue-500/10 dark:bg-blue-950/60 px-2.5 py-1 rounded-pill border border-blue-500/25 dark:border-blue-800/60">
                        <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                        Draft
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-300 bg-emerald-500/10 dark:bg-emerald-950/60 px-2.5 py-1 rounded-pill border border-emerald-500/25 dark:border-emerald-800/60">
                        <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                        Published
                      </span>
                    )}

                    {/* Action 3-dots Menu Button — a deleted notice is a record, not something to act on. */}
                    {!isDeleted && canEdit && (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenuId(isMenuOpen ? null : notice.uuid);
                          }}
                          className="grid h-8 w-8 place-items-center rounded-full text-ink-3 hover:bg-surface-2 hover:text-ink transition-colors cursor-pointer"
                          title="More options"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>

                        {/* Menu Dropdown */}
                        {isMenuOpen && (
                          <div
                            ref={menuRef}
                            className="absolute right-0 top-9 z-30 w-40 overflow-hidden rounded-[10px] bg-surface border border-line shadow-lg py-1 animate-pop-in"
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setActiveMenuId(null);
                                handleOpenEditModal(notice);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors text-left cursor-pointer"
                            >
                              <Pencil className="h-3.5 w-3.5 text-accent" />
                              Edit notice
                            </button>

                            <button
                              type="button"
                              onClick={() => void toggleNoticeDraft(notice)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors text-left cursor-pointer"
                            >
                              {isDraft ? (
                                <>
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                  Publish notice
                                </>
                              ) : (
                                <>
                                  <FileText className="h-3.5 w-3.5 text-orange-500" />
                                  Move to Draft
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                setActiveMenuId(null);
                                setDeleteConfirmId(notice.uuid);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors text-left cursor-pointer"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              Delete notice
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Date / Time */}
                  <div className="flex flex-col items-start  gap-1 text-xs text-ink-3 font-medium">
                    {/* Primary Date (Published / Created / Deleted) */}
                    <div className="flex items-center gap-1.5">
                      <Calendar
                        className={cn(
                          'h-3.5 w-3.5 shrink-0',
                          isDeleted
                            ? 'text-rose-500'
                            : isDraft
                            ? 'text-ink-3'
                            : 'text-emerald-600 dark:text-emerald-400',
                        )}
                      />
                      <span className="text-sm">
                        {isDeleted && notice.deletedAt ? (
                          <>
                            Deleted:{' '}
                            <span className="text-rose-600 dark:text-rose-400 font-semibold">
                              {formatInstituteDateTime(notice.deletedAt)}
                            </span>
                          </>
                        ) : isDraft ? (
                          <>
                            Created:{' '}
                            <span className="text-ink-2 font-bold">
                              {formatInstituteDateTime(notice.createdAt)}
                            </span>
                          </>
                        ) : (
                          <>
                            Published:{' '}
                            <span className="text-ink-2 font-bold">
                              {formatInstituteDateTime(notice.publishedAt || notice.createdAt)}
                            </span>
                          </>
                        )}
                      </span>
                    </div>

                    {/* Notice Date */}
                    <div
                      className={cn(
                        'flex items-center gap-1.5 transition-colors',
                        isNextUpcoming &&
                          'px-2.5 py-1 rounded-[8px] bg-indigo-100 dark:bg-indigo-900/50 border border-indigo-300 dark:border-indigo-700 shadow-2xs',
                      )}
                    >
                      <Calendar
                        className={cn(
                          'h-3.5 w-3.5 shrink-0',
                          isNextUpcoming
                            ? 'text-indigo-700 dark:text-indigo-300'
                            : 'text-indigo-500 dark:text-indigo-400',
                        )}
                      />
                      <span className="text-sm">
                        Notice Date:{' '}
                        <span
                          className={cn(
                            'font-bold',
                            isNextUpcoming
                              ? 'text-indigo-900 dark:text-indigo-100'
                              : 'text-ink-2',
                          )}
                        >
                          {formatInstituteDate(notice.noticeDate || today)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Pagination Footer ────────────────────────────────────────── */}
          {totalCount > 0 && (
            <div className="card border border-line rounded-card px-4 sm:px-5 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs bg-surface shadow-2xs mt-2">
              <span className="text-ink-3 font-medium">
                Showing {fromIndex} to {toIndex} of {totalCount} notices
              </span>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-ink-3 font-semibold text-xs">Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={(e) => updateFilters({ limit: Number(e.target.value) })}
                    className="h-7 px-2 text-xs bg-surface-2 border border-line rounded-[6px] text-ink focus:outline-none cursor-pointer font-medium"
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Page Navigation */}
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={safeCurrentPage <= 1}
                    onClick={() => setFilters({ page: Math.max(safeCurrentPage - 1, 1) })}
                    className="grid h-7 w-7 place-items-center rounded-[6px] border border-line bg-surface-2 text-ink hover:bg-surface-3 disabled:opacity-40 disabled:pointer-events-none transition-colors cursor-pointer"
                    title="Previous page"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setFilters({ page: p })}
                      className={cn(
                        'grid h-7 w-7 place-items-center rounded-[6px] text-xs font-bold transition-colors cursor-pointer',
                        p === safeCurrentPage
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'border border-line bg-surface-2 text-ink hover:bg-surface-3',
                      )}
                    >
                      {p}
                    </button>
                  ))}

                  <button
                    type="button"
                    disabled={safeCurrentPage >= totalPages}
                    onClick={() => setFilters({ page: Math.min(safeCurrentPage + 1, totalPages) })}
                    className="grid h-7 w-7 place-items-center rounded-[6px] border border-line bg-surface-2 text-ink hover:bg-surface-3 disabled:opacity-40 disabled:pointer-events-none transition-colors cursor-pointer"
                    title="Next page"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
        </div>
      )}

      {/* ── Institute Notices View ──────────────────────────────────────── */}
      {isInstituteTab && (
        <div className="flex flex-col gap-6">
          {/* ── Institute Notices List ───────────────────────────────────── */}
          {instituteIsLoading && (
            <div className="card p-6 rounded-card bg-surface border border-line">
              <SkeletonLines count={6} />
            </div>
          )}

          {!instituteIsLoading && instituteError && (
            <div className="card flex flex-col items-start gap-3 p-6 rounded-card bg-surface border border-line">
              <Alert tone="critical" title="Could not load institute notices">
                {instituteError.message}
              </Alert>
              <Button variant="secondary" size="sm" onClick={refetchInstitute}>
                Try again
              </Button>
            </div>
          )}

          {!instituteIsLoading && !instituteError && paginatedInstituteNotices.length === 0 && (
            <div className="card p-14 flex  flex-col items-center gap-4 text-center rounded-card bg-surface border border-line shadow-2xs">
              <div className="h-16 w-16 rounded-[16px] bg-blue-500/10 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-500/20 grid place-items-center">
                <Megaphone className="h-8 w-8 opacity-60" />
              </div>
              <div>
                <p className="text-[15px] font-bold text-ink">
                  {instituteDate ? 'No institute notices found for this date' : 'No institute notices posted yet'}
                </p>
                <p className="text-xs text-ink-3 font-medium mt-1">
                  {instituteDate
                    ? `No notices were published for ${formatInstituteDate(instituteDate)}. Try clearing the filter.`
                    : 'Notices published by your institute office will appear here.'}
                </p>
              </div>
              {instituteDate && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setInstituteDate('');
                    setInstitutePage(1);
                  }}
                >
                  Clear filter
                </Button>
              )}
            </div>
          )}

          {!instituteIsLoading && !instituteError && paginatedInstituteNotices.length > 0 && (
            <div className="flex flex-col gap-3">
              {paginatedInstituteNotices.map((notice) => {
                const cardTheme = NOTICE_TYPE_CARD_THEME[notice.noticeType as NoticeType] || NOTICE_TYPE_CARD_THEME.GENERAL;
                return (
                  <div
                    key={notice.uuid}
                    className={cn(
                      'card group relative flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 sm:p-5 rounded-[16px] transition-all bg-surface border border-line hover:border-indigo-500/30 hover:shadow-sm',
                      notice.isPinned && 'border-amber-400/60 dark:border-amber-500/40 bg-amber-50/20 dark:bg-amber-950/10',
                    )}
                  >
                    {/* Left Icon + Notice Main Info */}
                    <div className="flex items-start gap-3.5 sm:gap-4 flex-1 min-w-0">
                      <div
                        className={cn(
                          'grid h-12 w-12 shrink-0 place-items-center rounded-[12px] border transition-all duration-200 group-hover:scale-105 shadow-2xs',
                          cardTheme.iconBg,
                          cardTheme.iconBorder,
                          cardTheme.iconColor,
                        )}
                      >
                        <Megaphone className="h-5 w-5" />
                      </div>

                      <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-0.5 rounded-pill border',
                              cardTheme.badgeBg,
                              cardTheme.badgeBorder,
                              cardTheme.badgeColor,
                            )}
                          >
                            <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', cardTheme.dot)} />
                            {cardTheme.label}
                          </span>

                          {notice.isPinned && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-pill bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-300 dark:border-amber-800/60 shadow-2xs">
                              <Pin className="h-3 w-3 fill-current" />
                              Pinned
                            </span>
                          )}
                        </div>

                        <h3 className="text-[15px] font-bold text-ink tracking-tight leading-snug break-words">
                          {notice.title}
                        </h3>

                        {notice.body && (
                          <p className="text-[13px] text-ink-2 font-normal leading-relaxed break-words line-clamp-2">
                            {notice.body}
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-3 font-medium mt-0.5">
                          {notice.postedBy && (
                            <>
                              <span>By <strong className="text-ink-2 font-semibold">{notice.postedBy}</strong></span>
                              <span>•</span>
                            </>
                          )}
                          {notice.audienceLabel && (
                            <>
                              <span className="inline-flex items-center gap-1 font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40 px-2 py-0.5 rounded-[6px] border border-blue-200 dark:border-blue-800/50">
                                <Users className="h-3 w-3 inline text-blue-600 dark:text-blue-400" />
                                {notice.audienceLabel}
                              </span>
                              <span>•</span>
                            </>
                          )}
                          <span className="inline-flex items-center gap-1 text-ink-3">
                            <Calendar className="h-3 w-3 inline text-ink-3" />
                            {formatInstituteDate(notice.noticeDate)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right Date / Time */}
                    <div className="flex items-center md:flex-col md:items-end justify-between md:justify-center gap-1.5 sm:gap-2 shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-line/60">
                      <div className="flex items-center gap-1.5 text-xs text-ink-3 font-medium">
                        <Calendar className="h-3.5 w-3.5 shrink-0 text-indigo-500" />
                        <span className="text-sm">
                          Notice Date:{' '}
                          <span className="font-bold text-ink-2">
                            {formatInstituteDate(notice.noticeDate)}
                          </span>
                        </span>
                      </div>
                      <span className="text-xs text-ink-3 font-medium">
                        Posted {formatTimeAgo(notice.createdAt)}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Institute Pagination Footer */}
              {instituteTotalCount > 0 && (
                <div className="card border border-line rounded-card px-4 sm:px-5 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs bg-surface shadow-2xs mt-2">
                  <span className="text-ink-3 font-medium">
                    Showing {instituteFromIndex} to {instituteToIndex} of {instituteTotalCount} notices
                  </span>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-ink-3 font-semibold text-xs">Rows per page</span>
                      <select
                        value={instituteLimit}
                        onChange={(e) => {
                          setInstituteLimit(Number(e.target.value));
                          setInstitutePage(1);
                        }}
                        className="h-8 px-2 rounded-field border border-line bg-surface text-ink text-xs font-semibold outline-none cursor-pointer"
                      >
                        {[5, 10, 20, 50].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setInstitutePage((p) => Math.max(1, p - 1))}
                        disabled={institutePage <= 1}
                        className="p-1.5 rounded-[8px] border border-line text-ink hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                        title="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="px-2 font-bold text-ink">
                        {institutePage} / {instituteTotalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setInstitutePage((p) => Math.min(instituteTotalPages, p + 1))}
                        disabled={institutePage >= instituteTotalPages}
                        className="p-1.5 rounded-[8px] border border-line text-ink hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                        title="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Create / Edit Notice Modal ──────────────────────────────────────── */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="card w-full max-w-lg overflow-hidden bg-surface border border-line rounded-[20px] shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-line bg-surface-2 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-indigo-500/10 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400 border border-indigo-500/20">
                  <Megaphone className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-[16px] font-bold text-ink">
                    {editingNotice ? 'Edit Notice' : 'Create Notice'}
                  </h3>
                  <p className="text-xs text-ink-3 font-semibold">
                    {editingNotice
                      ? 'Update notice details for your class'
                      : 'Inform your students about events or reminders'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={resetForm}
                className="grid h-8 w-8 place-items-center rounded-full text-ink-3 hover:bg-surface hover:text-ink transition-colors cursor-pointer"
              >
                <X className="h-4.5 w-4.5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 flex flex-col gap-4 max-h-[75vh] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-ink-3/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-ink-3/50 transition-colors">
              {/* Who it is for */}
              <Select
                label="Who is it for"
                options={(Object.keys(NOTICE_AUDIENCE_LABEL) as NoticeAudience[]).map((audience) => ({
                  value: audience,
                  label: NOTICE_AUDIENCE_LABEL[audience],
                }))}
                value={formAudience}
                hint={
                  formAudience === 'STUDENTS'
                    ? 'Parents will not see this notice.'
                    : 'Parents of the class are notified when it is published.'
                }
                onChange={(e) => setFormAudience(e.target.value as NoticeAudience)}
              />

              {/* Target Class */}
              <Select
                label="Target Class"
                options={targetClassOptions}
                value={formSection}
                onChange={(e) => setFormSection(e.target.value)}
              />

              {/* Subject (Optional) */}
              <Select
                label="Subject (Optional)"
                options={formSubjectOptions}
                value={formSubject}
                onChange={(e) => setFormSubject(e.target.value)}
              />

              {/* Notice Date */}
              <DatePicker
                label="Notice Date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                min={!editingNotice ? today : undefined}
              />

              {/* Title */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-ink-2">
                  Notice Title <span className="text-crit">*</span>
                </label>
                <input
                  type="text"
                  id="notice-page-title-input"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder={placeholder}
                  maxLength={120}
                  className="w-full rounded-[10px] bg-surface-2 px-3.5 py-2.5 text-sm font-medium text-ink placeholder:text-ink-3 border border-line focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 transition-all"
                />
                <span className="text-xs text-ink-3 text-right font-medium">
                  {formTitle.length}/120
                </span>
              </div>

              {/* Body */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-bold text-ink-2">
                  Additional Details{' '}
                  <span className="text-ink-3 font-medium">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={formBody}
                  onChange={(e) => setFormBody(e.target.value)}
                  placeholder="Add any extra details or instructions here..."
                  className="w-full rounded-[10px] bg-surface-2 p-3.5 text-sm font-medium text-ink placeholder:text-ink-3 border border-line focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 transition-all resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="secondary" size="sm" onClick={resetForm} disabled={isSaving}>
                  Cancel
                </Button>
                {/* A draft stays a draft until it is published; a published notice
                    is updated in place and stays up. */}
                {(!editingNotice || editingNotice.status === 'DRAFT') && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void handleSaveNotice('DRAFT')}
                    disabled={!formTitle.trim() || isSaving}
                  >
                    <FileText className="h-3.5 w-3.5 mr-1.5" />
                    {editingNotice ? 'Save Draft' : 'Save as Draft'}
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => void handleSaveNotice('PUBLISHED')}
                  isLoading={isSaving}
                  disabled={!formTitle.trim()}
                >
                  <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                  {!editingNotice ? 'Post Notice' : editingNotice.status === 'DRAFT' ? 'Publish Notice' : 'Update Notice'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Dialog ───────────────────────────────────────────── */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="card w-full max-w-sm bg-surface border border-line rounded-[18px] shadow-2xl p-6 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-[12px] bg-crit-bg text-crit border border-crit">
                <AlertCircle className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-base font-bold text-ink">Delete {deletingNotice?.title ? `“${deletingNotice.title}”` : 'notice'}?</h3>
                <p className="text-xs text-ink-3 font-medium">
                  Your class will stop seeing this notice.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteConfirmId(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => void handleDelete()}
                isLoading={isDeleting}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
